import React, { useEffect, useRef, useState } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/services/apiClient";
import { API_URLS } from "@/constants/urls";
import { PROWEN_AUTH_USER, REGISTER_USER_EMAIL } from "@/constants";
import { storeAccessToken } from "@/helpers/localStorageHandlers";
import { useAuth } from "@/contexts/AuthContext";
import { apiErrorHandler } from "@/helpers/apiErrorHandler";

export default function OTPVerificationPage() {
    const navigate = useNavigate();

    const OTP_LENGTH = 6;
    const TIMER_DURATION = 60;

    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
    const [activeIndex, setActiveIndex] = useState(0);
    const [timer, setTimer] = useState(0);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);

    const inputRefs = useRef([]);

    const email = localStorage.getItem(REGISTER_USER_EMAIL);
    const { handleLogin } = useAuth();

    // 🔥 Timer logic
    useEffect(() => {
        if (timer <= 0) return;

        const interval = setInterval(() => {
            setTimer((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [timer]);

    useEffect(() => {
        inputRefs.current[activeIndex]?.focus();
    }, [activeIndex]);

    const handleChange = (value, index) => {
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < OTP_LENGTH - 1) {
            setActiveIndex(index + 1);
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            setActiveIndex(index - 1);
        }
    };

    const handlePaste = (e) => {
        const pasteData = e.clipboardData.getData("text").trim();
        if (!/^\d+$/.test(pasteData)) return;

        const newOtp = pasteData.slice(0, OTP_LENGTH).split("");
        setOtp(newOtp);
        setActiveIndex(Math.min(newOtp.length, OTP_LENGTH - 1));
    };

    const handleVerify = async () => {
        const finalOtp = otp.join("");

        if (finalOtp.length !== OTP_LENGTH) {
            toast.error("Please enter complete 6-digit OTP");
            return;
        }

        if (!email) {
            toast.error("User Email not found");
            return;
        }

        setIsVerifying(true);

        try {
            const result = await apiClient.post(API_URLS.VERIFY_OTP, {
                email,
                otp: finalOtp,
            });


            localStorage.clear(REGISTER_USER_EMAIL);
            toast.success("OTP verified successfully");
            
            handleLogin(result.data)


        } catch (error) {
            apiErrorHandler(error)
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResend = async () => {
        setIsResending(true);

        try {
            await apiClient.post(API_URLS.RESEND_OTP, { email });

            toast.success("OTP resent successfully");
            setTimer(TIMER_DURATION);
        } catch (error) {
            apiErrorHandler(error)
        } finally {
            setIsResending(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    return (
        <div className="min-h-screen flex">

            {/* LEFT PANEL (Same as Login/Register) */}
            <div className="hidden lg:flex lg:w-1/2 hero-gradient relative overflow-hidden">
                <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16">

                    <div className="flex items-center gap-3 mb-12">
                        <div className="w-12 h-12 rounded-xl bg-primary-foreground/10 backdrop-blur-sm flex items-center justify-center font-bold text-xl text-primary-foreground border border-primary-foreground/20">
                            P
                        </div>
                        <span className="font-semibold text-2xl text-primary-foreground">
                            Prowen
                        </span>
                    </div>

                    <h1 className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-6 leading-tight">
                        Secure Your<br />
                        <span className="text-accent-light">Account Access</span>
                    </h1>

                    <p className="text-lg text-primary-foreground/80 mb-8 max-w-md">
                        We’ve sent a verification code to your email. Enter it to activate your account securely.
                    </p>

                    <div className="space-y-4">
                        {[
                            "Secure Email Verification",
                            "Fast Account Activation",
                            "Encrypted Authentication",
                            "Protected Access",
                        ].map((feature, index) => (
                            <div key={index} className="flex items-center gap-3 text-primary-foreground/80">
                                <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                                    <Sparkles className="w-3 h-3 text-accent" />
                                </div>
                                <span>{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
                <div className="absolute top-20 right-20 w-64 h-64 bg-primary-light/10 rounded-full blur-3xl" />
            </div>

            {/* RIGHT PANEL - OTP FORM */}
            <div className="flex-1 flex items-center justify-center p-8 bg-background">
                <div className="w-full max-w-md">

                    <Card className="shadow-xl border-0">
                        <CardHeader className="text-center">
                            <CardTitle className="text-2xl">Verify Your Email</CardTitle>
                            <CardDescription>
                                Enter the 6-digit OTP sent to <br />
                                <span className="font-medium text-foreground">{email}</span>
                            </CardDescription>
                        </CardHeader>

                        <CardContent className="space-y-6">

                            {/* OTP Inputs */}
                            <div className="flex justify-between gap-2" onPaste={handlePaste}>
                                {otp.map((digit, index) => (
                                    <Input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        value={digit}
                                        maxLength={1}
                                        onChange={(e) => handleChange(e.target.value, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        className="text-center text-lg h-12"
                                    />
                                ))}
                            </div>

                            {/* Verify Button */}
                            <Button
                                className="w-full"
                                onClick={handleVerify}
                                disabled={isVerifying}
                            >
                                {isVerifying && (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                )}
                                Verify OTP
                            </Button>

                            {/* Resend Section */}
                            <div className="text-center text-sm text-muted-foreground">
                                {timer > 0 ? (
                                    <p>Resend OTP in {formatTime(timer)}</p>
                                ) : (
                                    <button
                                        onClick={handleResend}
                                        disabled={isResending}
                                        className="text-primary hover:underline font-medium"
                                    >
                                        {isResending ? "Resending..." : "Resend OTP"}
                                    </button>
                                )}
                            </div>

                        </CardContent>
                    </Card>

                    <p className="text-center text-xs text-muted-foreground mt-6">
                        © {new Date().getFullYear()} Prowen Technologies India Private Limited
                    </p>
                </div>
            </div>
        </div>
    );
}